import { MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import styled from "@emotion/styled";
import { useEffect, useRef } from "react";
import { MDSLoadingSpinner } from "@/mds/components/loading/loading-spinner";
import { LoadingContainer } from "@/components/note/editor/source-bar/floating-preview-content.styles";
import { NoteSourceWebSiteArtifactData } from "@/store/sources/types";
import { FloatingPreviewActions } from "@/components/note/editor/source-bar/floating-preview-actions";

type TProps = {
  isOpen: boolean;
  isLoading: boolean;
  artifactData: NoteSourceWebSiteArtifactData | null;
};

export const FloatingWebSitePreviewContent = ({ isLoading, artifactData, isOpen }: TProps) => {
  const { iframeRef } = useIframe({ artifactData, isOpen });

  return (
    <Content>
      {isLoading ? (
        <LoadingContainer>
          <MDSLoadingSpinner size={24} />
        </LoadingContainer>
      ) : (
        <>
          <SubjectText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
            {artifactData?.website_title}
          </SubjectText>
          <DeviceFrame>
            <PreviewIframe
              ref={iframeRef}
              sandbox="allow-same-origin"
              title={artifactData?.website_title ?? "Website"}
            />
          </DeviceFrame>
          {artifactData?.website_url && (
            <FloatingPreviewActions
              openBtnProps={{
                url: artifactData.website_url,
                title: "Open website",
              }}
            />
          )}
        </>
      )}
    </Content>
  );
};

const useIframe = ({
  artifactData,
  isOpen,
}: {
  artifactData: NoteSourceWebSiteArtifactData | null;
  isOpen: boolean;
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (isOpen && iframeRef.current && artifactData?.website_html_content) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(artifactData.website_html_content);
        doc.close();
      }
    }
  }, [artifactData?.website_html_content, isOpen]);

  return { iframeRef };
};

export const Content = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  overflowY: "auto",
  flexGrow: 1,
  minHeight: 0,
  width: "min(700px, 90vw)",
  height: "min(500px, 80vh)",
  maxWidth: "90vw",
  maxHeight: "80vh",
  paddingTop: theme.spacing["4"],
  paddingBottom: theme.spacing["4"],
}));

export const SubjectText = styled(MDSText)(
  ({ theme }) => `
  margin-bottom: 4px;
  flex-shrink: 0;
  padding-left: ${theme.spacing["4"]};
  padding-right: ${theme.spacing["4"]};
`
);

const DeviceFrame = styled.div({
  position: "relative",
  width: "100%",
  flex: "1 1 auto",
  minHeight: "0",
  backgroundColor: "white",
});

const PreviewIframe = styled.iframe({
  width: "100%",
  height: "100%",
  minHeight: "200px",
  border: "none",
  backgroundColor: "white",
});
